import {
  MIXED_CATEGORY,
  PACKING_QUEUE_MAX_LIMIT,
  type PackingQueueSort,
} from "@loot-os/fulfillment-core/order/packing-fold";
import { Button } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { downloadCsv, toCsv } from "@loot-os/ui/lib/csv";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { ErrorAlert } from "../../components/ErrorAlert";
import { CardsView } from "../../components/packing/CardsView";
import { ParityQueue } from "../../components/packing/ParityQueue";
import { FULFILLMENT_PANEL_CLASS } from "../../lib/fulfillmentPanel";
import { exportFileName, pullSheetRows } from "../../lib/orderExports";
import { openOrdersInTabs } from "../../lib/orderTabs";
import { selectedIds, toSel } from "../../lib/packing";
import { exportMissingCards, exportPackingSlips } from "../../lib/packingExports";
import { useTRPC } from "../../lib/trpc";

const route = getRouteApi("/app/fulfillment/packing");

/** The parity grid holds the whole queue, so the selection window has something to slide over. */
/** Everything the server allocates, so Offset can reach any bag it judged. */
const PARITY_PAGE = PACKING_QUEUE_MAX_LIMIT;

const _COLUMNS: Array<{ key: PackingQueueSort | null; label: string; numeric?: boolean }> = [
  { key: null, label: "" },
  { key: null, label: "Order" },
  { key: null, label: "Shopper" },
  { key: "createdAt", label: "Age" },
  { key: "lineCount", label: "Cards", numeric: true },
  { key: "value", label: "Value", numeric: true },
  { key: null, label: "Ships to" },
  { key: "tenant", label: "Tenant" },
  { key: null, label: "" },
];

const _AVAILABILITY: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  in_stock: { label: "In stock", variant: "secondary" },
  short: { label: "Short", variant: "destructive" },
  out_of_stock: { label: "Out of stock", variant: "destructive" },
  unknown: { label: "Unknown", variant: "outline" },
};

/**
 * The packing queue: the processing bags oldest first, a positional batch window, and the
 * combined pick sheet for whatever is selected. Read-only: a boxed bag is still marked `PACKED` in
 * Retool, a Loot OS withdrawal on the fulfillment console's order page.
 */
export function PackingPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const trpc = useTRPC();

  const offset = search.offset ?? 0;
  const _limit = search.limit ?? 20;
  // The assignment size, independent of the page size since they answer different questions.
  const batch = search.batch ?? 12;
  const tab = search.tab ?? "unpacked";
  const selection = selectedIds(search.sel);

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) });

  const queue = useQuery(
    trpc.platform.fulfillment.packing.queue.queryOptions({
      query: search.q,
      sort: search.sort,
      direction: search.dir,
      category: search.cat,
      includeOutOfStock: search.oos ?? false,
      slaWeeks: search.sla ?? 0,
      packState: tab === "partial" ? "partial" : tab === "unpacked" ? "unpacked" : undefined,
      createdFrom: search.from ? new Date(search.from) : undefined,
      createdTo: search.to ? new Date(search.to) : undefined,
      // The grid holds the whole queue; Offset and # of Orders slide a selection
      // window over it rather than paging it.
      offset: 0,
      limit: PARITY_PAGE,
    }),
  );

  const sheet = useQuery({
    ...trpc.platform.fulfillment.packing.pickSheet.queryOptions({ orderIds: selection }),
    enabled: search.view === "sheet" && selection.length > 0,
  });

  const rows = queue.data?.rows ?? [];
  const _total = queue.data?.total ?? 0;

  // The window the batch checkbox takes, as Retool does it: Offset and # of Orders slide over
  // the loaded list rather than paging it.
  const windowRows = rows.slice(offset, offset + batch);
  const allShownSelected = rows.length > 0 && rows.every((row) => selection.includes(row.orderId));
  const batchSelected =
    windowRows.length > 0 && windowRows.every((row) => selection.includes(row.orderId));

  const toggle = (orderId: string) => {
    const selected = selection.includes(orderId);
    const next = selected ? selection.filter((id) => id !== orderId) : [...selection, orderId];
    setSearch({ sel: toSel(next) });
  };

  /** The header checkbox: adds or removes the shown rows, leaving any other selection alone. */
  const toggleAllShown = () => {
    const shown = rows.map((row) => row.orderId);
    const next = allShownSelected
      ? selection.filter((id) => !shown.includes(id))
      : [...new Set([...selection, ...shown])];
    setSearch({ sel: toSel(next) });
  };

  const missingCards = useQuery({
    ...trpc.platform.fulfillment.packing.missingCards.queryOptions({
      query: search.q,
      category: search.cat,
      includeOutOfStock: true,
      createdFrom: search.from ? new Date(search.from) : undefined,
      createdTo: search.to ? new Date(search.to) : undefined,
    }),
    enabled: tab === "missing",
  });

  /**
   * The slips cover the selected bags; the poster covers the whole queue, as Retool does.
   *
   * Fetched by id rather than read off the grid. Reading the grid meant a bag selected before a
   * filter changed was still counted in the footer and still drawn in the card view, but silently
   * missing from the download — three answers to "what is selected".
   */
  const queryClient = useQueryClient();
  const onExportSlips = async () => {
    exportPackingSlips(
      await queryClient.fetchQuery(
        trpc.platform.fulfillment.orders.packingSlips.queryOptions({ orderIds: selection }),
      ),
    );
  };

  /** The order list's pull sheet over the selected bags, read when pressed. */
  const onExportPullSheet = async () => {
    const pullSheet = await queryClient.fetchQuery(
      trpc.platform.fulfillment.orders.pullSheet.queryOptions({ orderIds: selection }),
    );
    if (pullSheet.length === 0) {
      toast.error("No cards to pull for the selected bags. Nothing was downloaded.");
      return;
    }
    downloadCsv(exportFileName("export-pullSheet"), toCsv(pullSheetRows(pullSheet)));
  };

  const onGenerateMissingCards = async () => {
    const result = await missingCards.refetch();
    exportMissingCards(result.data?.cards ?? []);
  };

  const openInTabs = () => openOrdersInTabs(selection);

  const _sortBy = (key: PackingQueueSort) => {
    const sameColumn = (search.sort ?? "createdAt") === key;
    const dir = sameColumn && (search.dir ?? "asc") === "asc" ? "desc" : "asc";
    setSearch({ sort: key, dir, offset: 0 });
  };

  // From the server, and deliberately not from `rows`: options built from the visible rows
  // collapse to whatever is already selected, so you cannot switch games without clearing first.
  const categories = useQuery(
    trpc.platform.fulfillment.packing.categories.queryOptions({
      query: search.q,
      packState: tab === "partial" ? "partial" : tab === "unpacked" ? "unpacked" : undefined,
      createdFrom: search.from ? new Date(search.from) : undefined,
      createdTo: search.to ? new Date(search.to) : undefined,
    }),
  );
  const categoryOptions = [...(categories.data ?? []), MIXED_CATEGORY];

  const rootClass = `${FULFILLMENT_PANEL_CLASS} flex flex-col gap-6`;

  if (search.view === "sheet") {
    return (
      <div className={rootClass}>
        <div className="flex items-start justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Cards View</h1>
            <p className="text-sm text-muted-foreground">
              Every card in the selected orders, one cell per card, sorted by name.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSearch({ view: undefined })}>
              Back to queue
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="size-4" /> Print
            </Button>
          </div>
        </div>
        {sheet.error ? <ErrorAlert message={sheet.error.message} /> : null}
        {sheet.isPending ? <Skeleton className="h-64 w-full" /> : null}
        {sheet.data ? <CardsView units={sheet.data.units} /> : null}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Packing</h1>
          <p className="text-sm text-muted-foreground">
            Laid out as Fulfillment/TCG Pack Orders. Marking cards packed still happens there.
          </p>
        </div>
      </div>
      {queue.error ? <ErrorAlert message={queue.error.message} /> : null}
      <ParityQueue
        data={queue.data}
        isPending={queue.isPending}
        batch={batch}
        offset={offset}
        slaWeeks={search.sla ?? 0}
        scanTruncated={queue.data?.scanTruncated ?? false}
        category={search.cat}
        categories={categoryOptions}
        includeOutOfStock={search.oos ?? false}
        batchSelected={batchSelected}
        allShownSelected={allShownSelected}
        selection={selection}
        tab={tab}
        from={search.from}
        to={search.to}
        onToggleRow={toggle}
        onToggleAllShown={toggleAllShown}
        onTab={(next) => setSearch({ tab: next === "unpacked" ? undefined : next, offset: 0 })}
        onFrom={(next) => setSearch({ from: next })}
        onTo={(next) => setSearch({ to: next })}
        onOpenInTabs={openInTabs}
        onExportSlips={() => {
          onExportSlips().catch((error: unknown) =>
            toast.error(error instanceof Error ? error.message : "Export Packing Slips failed."),
          );
        }}
        onExportPullSheet={() => {
          onExportPullSheet().catch((error: unknown) =>
            toast.error(error instanceof Error ? error.message : "Export Pull Sheet failed."),
          );
        }}
        onGenerateMissingCards={onGenerateMissingCards}
        onBatch={(next) => setSearch({ batch: next })}
        onOffset={(next) => setSearch({ offset: next })}
        onSlaWeeks={(next) => setSearch({ sla: next === 0 ? undefined : next })}
        onCategory={(next) => setSearch({ cat: next, offset: 0 })}
        onIncludeOutOfStock={(next) => setSearch({ oos: next || undefined, offset: 0 })}
        onSelectBatch={(selected) =>
          setSearch({
            sel: selected ? toSel(windowRows.map((row) => row.orderId)) : undefined,
          })
        }
        onOpenCards={() => setSearch({ view: "sheet" })}
      />
    </div>
  );
}
